"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CellValue = void 0;
const react_1 = __importStar(require("react"));
const interface_1 = require("../../interface");
const widget_context_1 = require("../../context/widget_context");
const hooks_1 = require("../../hooks");
const cell_text_1 = require("./cell_text");
const cell_options_1 = require("./cell_options");
const cell_attachment_1 = require("./cell_attachment");
const cell_rating_1 = require("./cell_rating");
const cell_member_1 = require("./cell_member");
const cell_checkbox_1 = require("./cell_checkbox");
const cell_phone_1 = require("./cell_phone");
const cell_url_1 = require("./cell_url");
const cell_email_1 = require("./cell_email");
const cell_link_1 = require("./cell_link");
const cell_enhance_text_1 = require("./cell_enhance_text");
const core_1 = require("../../core");
const cell_multi_text_1 = require("./cell_multi_text");
/**
 * Display the UI style for recording the cells of the specified field,
 * all types of fields are now supported.
 *
 * @param props.recordId The recordId from which to render a cell.
 * @param props.fieldId The fieldId from which to render a cell.
 * @param props.cellValue The cell value to render.
 * Either record or cellValue must be provided to the CellValue. If both are provided, record will be used.
 * @param props.className Additional class names to apply to the cell renderer container, separated by spaces.
 * @param props.style Additional styles to apply to the cell renderer container.
 * @param props.cellClassName Additional class names to apply to the cell itself, separated by spaces.
 * @param props.cellStyle Additional styles to apply to the cell itself.
 * @returns
 *
 * #### Example
 *
 * **Method 1**
 *
 * Use recordId,fieldId render CellValue UI,
 * Rendering CellValue UI by recordId, fieldId, e.g. focus cell's rendering cell display UI.
 *
 * ```tsx
 * import React from 'react';
 * import { useActiveCell, CellValue } from '@apitable/widget-sdk';
 *
 * export const CellValueUI = () => {
 *   const activeCell = useActiveCell();
 *   if (!activeCell) {
 *     return <p>Cells without activation</p>
 *   }
 *  const { recordId, fieldId } = activeCell;
 *   return (
 *     <CellValue
 *       className="wrapperClass"
 *       cellClassName="cellClass"
 *       recordId={recordId}
 *       fieldId={fieldId}
 *     />
 *   )
 * }
 * ```
 *
 *  **Method 2**
 *
 * Render CellValue UI by cellValue, fieldId,
 * e.g. merge or difference set calculation for multiple cells data in the same column, return data in cellValue format.
 *
 * ```tsx
 * import React from 'react';
 * import { useActiveCell, CellValue } from '@apitable/widget-sdk';
 *
 * export const CellValueUI = ({ cellValue }) => {
 *   const activeCell = useActiveCell();
 *   if (!activeCell) {
 *     return <p>Cells without activation</p>
 *   }
 *  const { fieldId } = activeCell;
 *   return (
 *     <CellValue
 *       className="wrapperClass"
 *       cellClassName="cellClass"
 *       cellValue={cellValue}
 *       fieldId={fieldId}
 *     />
 *   )
 * }
 * ```
 */
const CellValue = (props) => {
    var _a;
    const context = (0, react_1.useContext)(widget_context_1.WidgetContext);
    const { recordId, fieldId, className, style, cellClassName, cellStyle, cellValue: cellValueData } = props;
    const wrapperProps = { className, style };
    const cellProps = { cellClassName, cellStyle };
    const activeRecord = (0, hooks_1.useRecord)(recordId);
    const activeField = (0, hooks_1.useField)(fieldId);
    let type = activeField === null || activeField === void 0 ? void 0 : activeField.type;
    let property = (_a = activeField === null || activeField === void 0 ? void 0 : activeField.fieldData) === null || _a === void 0 ? void 0 : _a.property;
    const state = context.widgetStore.getState();
    if (!activeField || !type || !state) {
        return null;
    }
    let cellValue = cellValueData || activeRecord.getCellValue(fieldId);
    if (type === interface_1.FieldType.MagicLookUp) {
        const realField = core_1.Field.bindModel(activeField.fieldData, state).getLookUpEntityField();
        const realType = realField === null || realField === void 0 ? void 0 : realField.type;
        if (!realType) {
            return null;
        }
        type = (0, core_1.getFieldTypeString)(realType);
        if (type === interface_1.FieldType.Attachment ||
            type === interface_1.FieldType.MultiSelect ||
            type === interface_1.FieldType.Member ||
            type === interface_1.FieldType.MagicLink) {
            cellValue = cellValue ? cellValue[0] : null;
        }
        if (type === interface_1.FieldType.MultiSelect ||
            type === interface_1.FieldType.SingleSelect ||
            type === interface_1.FieldType.Checkbox) {
            property = realField === null || realField === void 0 ? void 0 : realField.property;
        }
        if (type === interface_1.FieldType.Email ||
            type === interface_1.FieldType.URL ||
            type === interface_1.FieldType.Phone ||
            type === interface_1.FieldType.Rating) {
            type = interface_1.FieldType.Text;
        }
    }
    const handleCellValue = () => {
        return cellValueData ? activeField.convertCellValueToString(cellValueData) : activeRecord.getCellValueString(fieldId);
    };
    switch (type) {
        case interface_1.FieldType.SingleText:
            return react_1.default.createElement(cell_enhance_text_1.CellEnhanceText, Object.assign({ text: handleCellValue() }, cellProps));
        case interface_1.FieldType.Text:
            return react_1.default.createElement(cell_multi_text_1.CellMultiText, Object.assign({ text: handleCellValue() }, cellProps));
        case interface_1.FieldType.Currency:
        case interface_1.FieldType.DateTime:
        case interface_1.FieldType.CreatedTime:
        case interface_1.FieldType.LastModifiedTime:
        case interface_1.FieldType.Percent:
        case interface_1.FieldType.AutoNumber:
        case interface_1.FieldType.Number:
        case interface_1.FieldType.Formula:
            return react_1.default.createElement(cell_text_1.CellText, Object.assign({ text: handleCellValue() }, cellProps));
        case interface_1.FieldType.MultiSelect:
        case interface_1.FieldType.SingleSelect:
            return react_1.default.createElement(cell_options_1.CellOptions, Object.assign({ options: property === null || property === void 0 ? void 0 : property.options, selectOptions: cellValue }, cellProps, wrapperProps));
        case interface_1.FieldType.Attachment:
            return react_1.default.createElement(cell_attachment_1.CellAttachment, Object.assign({ files: cellValue }, cellProps, wrapperProps));
        case interface_1.FieldType.Rating:
            return react_1.default.createElement(cell_rating_1.CellRating, Object.assign({ field: property, count: cellValue }, cellProps, wrapperProps));
        case interface_1.FieldType.Member:
        case interface_1.FieldType.LastModifiedBy:
        case interface_1.FieldType.CreatedBy:
            return react_1.default.createElement(cell_member_1.CellMember, Object.assign({ members: cellValue }, cellProps, wrapperProps));
        case interface_1.FieldType.Checkbox:
            return react_1.default.createElement(cell_checkbox_1.CellCheckbox, Object.assign({ field: property, checked: cellValue }, cellProps, wrapperProps));
        case interface_1.FieldType.Phone:
            return react_1.default.createElement(cell_phone_1.CellPhone, Object.assign({ value: cellValue }, cellProps));
        case interface_1.FieldType.Email:
            return react_1.default.createElement(cell_email_1.CellEmail, Object.assign({ value: cellValue }, cellProps));
        case interface_1.FieldType.URL:
            return react_1.default.createElement(cell_url_1.CellUrl, Object.assign({ value: cellValue }, cellProps));
        case interface_1.FieldType.MagicLink:
            return react_1.default.createElement(cell_link_1.CellLink, Object.assign({ options: cellValue }, cellProps, wrapperProps));
        default:
            return react_1.default.createElement("span", null, "Not support");
    }
};
exports.CellValue = CellValue;
